import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'

import type {
  PriorityPaymentRow,
  PriorityPaymentsResponse
} from '@/Network/Types/Responses/PriorityPaymentsResponses'
import { usePriorityPaymentsStore } from '@/pinia-store/usePriorityPaymentsStore'

import { priorityColumnDefinitions } from '../constants'
import type { PriorityColumnServerFilter, PriorityGroupColumn } from '../constants'
import { priorityFiltersToQuery } from '../filters'
import {
  PRIORITY_KANBAN_COLUMN_PAGE_SIZE,
  PRIORITY_KANBAN_MAX_KEPT_PAGES,
  priorityColumnQueryFilter,
  priorityColumnTotal
} from '../kanbanColumns'
import type { PriorityGroupBy } from './usePriorityGrouping'

interface ColumnBucket {
  rows: PriorityPaymentRow[]
  /** Páginas de N já pedidas — a próxima do "Ver mais" é `pages + 1`. */
  pages: number
  /** `count` do servidor para o grupo; `null` antes de a 1ª página chegar. */
  count: number | null
}

/**
 * Kanban de Pagamentos prioritários com carga POR COLUNA (a listagem principal
 * traz só a página da Lista/Tabela, 25 linhas misturadas).
 *
 * - Cada nova resposta da listagem (filtro, aba, período, busca, refresh,
 *   ação na linha) recarrega as colunas com total > 0, em paralelo.
 * - Recorte (filtros + agrupamento) DIFERENTE: as colunas voltam à 1ª página.
 * - MESMO recorte (refresh de 60 s, "Atualizar", ação na linha): cada coluna
 *   mantém as páginas que já tinha, numa requisição só, até
 *   `PRIORITY_KANBAN_MAX_KEPT_PAGES` (200 linhas) — o refresh não "encolhe"
 *   a coluna que a pessoa expandiu.
 * - Fora do Kanban não busca nada.
 */
export function usePriorityKanbanColumns(options: {
  groupBy: Readonly<Ref<PriorityGroupBy>>
  isActive: Readonly<Ref<boolean>>
  onLoadMoreError: (error: unknown) => void
}): {
  rows: ComputedRef<PriorityPaymentRow[]>
  totals: ComputedRef<Partial<Record<string, number>>>
  hasMore: ComputedRef<Partial<Record<string, boolean>>>
  loading: Ref<Partial<Record<string, boolean>>>
  loadMore: (columnKey: string) => Promise<void>
} {
  const store = usePriorityPaymentsStore()

  const buckets = ref<Record<string, ColumnBucket>>({})
  const loadedScope = ref<string | null>(null)
  const loading = ref<Partial<Record<string, boolean>>>({})
  /** Guard de corrida: recarga nova invalida respostas de "Ver mais" antigas. */
  let generation = 0

  // As colunas só mudam com o agrupamento; o estado ao vivo não entra aqui.
  const definitions = computed(() =>
    priorityColumnDefinitions(options.groupBy.value, { nowMs: null, config: null })
  )

  const scopeKey = (): string => {
    const query = priorityFiltersToQuery(store.filters, 1)
    delete query.page
    delete query.limit
    return JSON.stringify([options.groupBy.value, query])
  }

  const isCurrent = computed(() => loadedScope.value !== null && loadedScope.value === scopeKey())

  const totals = computed<Partial<Record<string, number>>>(() => {
    const response = store.response
    if (!options.isActive.value || !response) return {}
    const result: Partial<Record<string, number>> = {}
    for (const column of definitions.value) {
      const total = priorityColumnTotal(column, response, store.filters)
      if (total !== undefined) result[column.key] = total
    }
    return result
  })

  async function loadColumn(
    column: PriorityGroupColumn,
    pages: number,
    previous: ColumnBucket | undefined
  ): Promise<ColumnBucket | undefined> {
    const filter = priorityColumnQueryFilter(column, store.filters)
    if (!filter) return undefined
    const total = store.response ? priorityColumnTotal(column, store.response, store.filters) : 0
    if (total === 0) return { rows: [], pages: 0, count: 0 }
    try {
      const data = await store.fetchGroupPage(filter, 1, pages * PRIORITY_KANBAN_COLUMN_PAGE_SIZE)
      return { rows: data.data, pages, count: data.count }
    } catch {
      // Refresh que falha não apaga a coluna; sem nada antes, a coluna fica
      // vazia com o "Ver mais" servindo de nova tentativa.
      return previous ?? { rows: [], pages: 0, count: total ?? null }
    }
  }

  async function reload(): Promise<void> {
    if (!options.isActive.value || !store.response) return
    const scope = scopeKey()
    const keep = scope === loadedScope.value
    const previous = keep ? buckets.value : {}
    const current = ++generation
    loading.value = {}
    const entries = await Promise.all(
      definitions.value.map(async (column) => {
        const pages = Math.min(
          Math.max(previous[column.key]?.pages ?? 1, 1),
          PRIORITY_KANBAN_MAX_KEPT_PAGES
        )
        return [column.key, await loadColumn(column, pages, previous[column.key])] as const
      })
    )
    if (current !== generation) return
    const next: Record<string, ColumnBucket> = {}
    for (const [key, bucket] of entries) if (bucket) next[key] = bucket
    buckets.value = next
    loadedScope.value = scope
  }

  /** Coluna pronta para o "Ver mais": recorte carregado, sem busca em voo. */
  function loadMoreTarget(
    columnKey: string
  ): { bucket: ColumnBucket; filter: PriorityColumnServerFilter } | null {
    if (loading.value[columnKey] || !isCurrent.value) return null
    const column = definitions.value.find((candidate) => candidate.key === columnKey)
    const bucket = buckets.value[columnKey]
    const filter = column ? priorityColumnQueryFilter(column, store.filters) : null
    return bucket && filter ? { bucket, filter } : null
  }

  const appendPage = (bucket: ColumnBucket, data: PriorityPaymentsResponse, page: number) => {
    const seen = new Set(bucket.rows.map((row) => row.doctorStationId))
    return {
      rows: [...bucket.rows, ...data.data.filter((row) => !seen.has(row.doctorStationId))],
      pages: page,
      count: data.count
    }
  }

  async function loadMore(columnKey: string): Promise<void> {
    const target = loadMoreTarget(columnKey)
    if (!target) return
    const current = generation
    const nextPage = target.bucket.pages + 1
    loading.value = { ...loading.value, [columnKey]: true }
    try {
      const data = await store.fetchGroupPage(
        target.filter,
        nextPage,
        PRIORITY_KANBAN_COLUMN_PAGE_SIZE
      )
      if (current !== generation) return
      buckets.value = { ...buckets.value, [columnKey]: appendPage(target.bucket, data, nextPage) }
    } catch (error) {
      if (current === generation) options.onLoadMoreError(error)
    } finally {
      if (current === generation) loading.value = { ...loading.value, [columnKey]: false }
    }
  }

  /**
   * Linhas do Kanban: a união das colunas, sem repetição (o tique pode mover
   * uma linha de grupo; o Kanban a coloca pela regra AO VIVO). Antes da 1ª
   * carga do recorte atual, as linhas da listagem — a tela nunca fica vazia
   * esperando as colunas. Colunas sem filtro no servidor ("Sem SLA") seguem
   * vindo da listagem.
   */
  const rows = computed<PriorityPaymentRow[]>(() => {
    if (!isCurrent.value) return store.rows
    const seen = new Set<number>()
    const result: PriorityPaymentRow[] = []
    const push = (row: PriorityPaymentRow) => {
      if (seen.has(row.doctorStationId)) return
      seen.add(row.doctorStationId)
      result.push(row)
    }
    for (const column of definitions.value) {
      const bucket = buckets.value[column.key]
      if (bucket) bucket.rows.forEach(push)
      else if (!column.serverFilter) store.rows.filter(column.matches).forEach(push)
    }
    return result
  })

  const hasMore = computed<Partial<Record<string, boolean>>>(() => {
    if (!isCurrent.value) return {}
    const result: Partial<Record<string, boolean>> = {}
    for (const [key, bucket] of Object.entries(buckets.value)) {
      const target = bucket.count ?? totals.value[key] ?? 0
      result[key] = bucket.rows.length < target
    }
    return result
  })

  watch(
    [() => store.response, options.groupBy, options.isActive],
    () => {
      void reload()
    },
    { immediate: true }
  )

  return { rows, totals, hasMore, loading, loadMore }
}
